// Cart drawer: slides in from the right on desktop and fills the screen on mobile.
// The page behind stays where it is; closing (×, Continue Shopping, Escape or the backdrop) returns to it.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CartPage from '../pages/CartPage';
import { headerBottomPx } from '../utils/headerOverlay';

export default function CartDrawer({ open, onClose }) {
  const { t } = useTranslation();
  // Navigating to another page (for example My Orders from the cash confirmation) closes the drawer, so its backdrop
  // and scroll lock do not stay over the new page
  const { pathname } = useLocation();
  const pathWhenOpened = useRef(pathname);
  const wasOpen = useRef(false);
  useEffect(() => {
  if (open && !wasOpen.current) {
    pathWhenOpened.current = pathname;
  }

  wasOpen.current = open;

  if (open && pathname !== pathWhenOpened.current) {
    onClose();
  }
}, [open, pathname, onClose]); 
useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Start the drawer at the header's real bottom edge (mobile and desktop), so it never covers the header
  const [headerBottom, setHeaderBottom] = useState(null);
  useEffect(() => {
    if (!open) return;
    setHeaderBottom(headerBottomPx());
  }, [open]);

  // Stop the page behind the modal from scrolling while it is open
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 select-none" onClick={onClose}>
      <aside role="dialog" aria-label={t('cart.title')} style={headerBottom != null ? { top: headerBottom } : undefined} className="fixed left-4 right-4 top-16 bottom-4 sm:left-auto sm:right-0 sm:w-[28rem] flex flex-col overflow-hidden bg-white text-gray-800 rounded-xl shadow-lg border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end border-b border-gray-200 px-4 py-2 shrink-0">
          <button type="button" onClick={onClose} aria-label={t('cart.closeCart')} className="text-2xl px-2">×</button>
        </div>
        {/* The list scrolls; the page's own card styling is removed inside the drawer */}
        <div className="flex-1 overflow-y-auto px-4 pt-2 [&>div]:shadow-none [&>div]:p-0 [&>div]:rounded-none [&>div]:max-w-none [&>div>div:first-child]:-mx-0 [&>div>div:first-child]:-mt-0">
          <CartPage />
        </div>
        <div className="px-4 py-2 border-t border-gray-200 shrink-0">
          <button type="button" onClick={onClose} className="w-full border rounded py-2 hover:bg-gray-100">{t('cart.continueShopping')}</button>
        </div>
      </aside>
    </div>
  );
}
